// views/Dashboard/PainelExecutivo/sections/comercial/SecaoComercial.tsx
import React, { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import type { FiltrosDashboard } from '../../types';
import { calcularJanelaPeriodoDashboard, rotuloPeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import { janelaParaPeriodoComercial } from '../../../../../src/lib/dashboard/janelaParaPeriodoComercial';
import { filtrarPorDepartamento } from '../../../../../src/lib/dashboard/filtrarNegociacoesPorDepartamento';
import { computeEventFunnel, taxaReprovacao } from '../../../../CRM/Analytics/eventFunnel';
import { fmtKwh, fmtNum } from '../../../../../src/lib/funnelUtils';
import { useComercialDashboardData } from './useComercialDashboardData';
import LoadingSpinner from '../../../../../src/components/LoadingSpinner';
import FunnelChart from '../../../../CRM/Analytics/blocks/FunnelChart';
import ComparativoResumo from '../../components/ComparativoResumo';
import ListaNegociacoesModal from './ListaNegociacoesModal';
import { concentracaoKwh, linhasDaEtapa, volumeKwhPorCanal } from '../../../../../src/lib/dashboard/negociacoesDaEtapa';
import type { ConcentracaoKwh, LinhaNegociacao } from '../../../../../src/lib/dashboard/negociacoesDaEtapa';
import { baixarPlanilhas } from '../../../../Financeiro/PainelExecutivo/lib/baixarPlanilhas';
import { slugArquivo } from '../../../../Financeiro/PainelExecutivo/lib/planilhasPainel';
import { planilhaNegociacoes } from './planilhaNegociacoes';
import { useToast } from '../../../../../src/contexts/ToastContext';
import SeletorDepartamentos from '../../components/SeletorDepartamentos';
import SeletorContagemFunil from '../../components/SeletorContagemFunil';
import { aplicarContagemFunil } from '../../../../../src/lib/dashboard/funilJornadaNoPeriodo';
import type { ItemComparativo } from '../../components/ComparativoResumo';

interface Props {
  filtros: FiltrosDashboard;
  /** O seletor de canal mora AQUI, e não na barra da casca, porque é esta seção
   *  que tem as negociações — é delas que sai o kWh mostrado em cada opção. */
  onChange: (filtros: FiltrosDashboard) => void;
}

interface ResumoEtapa { concentracao: ConcentracaoKwh | null; vindasDeOutroMes: number }

const CardComercial: React.FC<{ titulo: string; qtde: number; kwh: number; resumo: ResumoEtapa | null; onClick: () => void; saida?: boolean; nota?: string }> = ({ titulo, qtde, kwh, resumo, onClick, saida, nota }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`${titulo}: ${fmtNum(qtde)} negociações${nota ? `. ${nota}` : ''}. Ver a lista`}
    className={`group text-left w-full bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 ${saida ? 'hover:border-red-300 dark:hover:border-red-500/40' : 'hover:border-primary/40'} hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-all`}
  >
    <p title={titulo} className={`text-xs font-semibold uppercase tracking-wide truncate ${saida ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>{titulo}</p>
    <p className="text-lg font-bold text-gray-900 dark:text-white mt-0.5">{fmtNum(qtde)}</p>
    <p className="text-xs font-semibold text-blue-600 dark:text-blue-400">{fmtKwh(kwh)}</p>
    {/* #6331: saída do funil — a taxa é sobre as decisões do período, não sobre a prospecção. */}
    {nota && <p className="text-[11px] font-semibold leading-tight text-red-600 dark:text-red-400 mt-1" title="Reprovados ÷ (aprovados + reprovados) no período. Reprovado não é a etapa seguinte à aprovação: é onde a negociação sai do funil.">{nota}</p>}
    {/* O kWh do funil costuma vir de poucas negociações: sem este aviso, um
        cliente grande faz o mês parecer excepcional. */}
    {resumo?.concentracao?.concentrado && (
      <p className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 mt-1 truncate" title={`${resumo.concentracao.cliente} responde por ${(resumo.concentracao.fracao * 100).toFixed(1)}% do kWh desta etapa`}>
        ⚠ 1 negociação = {(resumo.concentracao.fracao * 100).toFixed(1)}% do kWh
      </p>
    )}
    {/* Negócio que começou num mês e chegou nesta etapa em outro: é o que faz o
        funil do mês parecer contraditório (assinado sem ter sido enviado aqui). */}
    {!!resumo?.vindasDeOutroMes && (
      <p className="text-[11px] font-semibold text-sky-600 dark:text-sky-400 mt-1 truncate" title="A etapa anterior dessas negociações aconteceu em outro mês">
        {resumo.vindasDeOutroMes} {resumo.vindasDeOutroMes === 1 ? 'veio' : 'vieram'} de outro mês
      </p>
    )}
    <p className="text-[11px] font-semibold text-primary mt-1 inline-flex items-center gap-0.5 opacity-70 group-hover:opacity-100 transition-opacity">
      Ver negociações <ChevronRight size={12} />
    </p>
  </button>
);

const SecaoComercial: React.FC<Props> = ({ filtros, onChange }) => {
  const { dados, profilesById, loading, erro } = useComercialDashboardData();
  const { addToast } = useToast();
  const [etapaAberta, setEtapaAberta] = useState<string | null>(null);
  const [exportando, setExportando] = useState(false);
  const hoje = useMemo(() => new Date(), []);

  const dadosFiltrados = useMemo(
    () => filtrarPorDepartamento(dados, profilesById, filtros.departamentos),
    [dados, profilesById, filtros.departamentos]
  );

  const { periodo, customRange, umMes } = useMemo(() => {
    const janela = calcularJanelaPeriodoDashboard(filtros.periodo, hoje);
    return {
      ...janelaParaPeriodoComercial(janela, hoje),
      umMes: !!janela && janela.inicio.getFullYear() === janela.fim.getFullYear() && janela.inicio.getMonth() === janela.fim.getMonth(),
    };
  }, [filtros.periodo, hoje]);

  // 'evento' = cada etapa pela data dela; 'jornada' = só o que teve todas as
  // etapas dentro do período (ver funilJornadaNoPeriodo.ts). Tudo que é número
  // nesta tela passa por aqui: cards, gráfico, lista, comparativo e o seletor de canal.
  const contagem = filtros.contagemComercial;
  const soNoPeriodo = contagem === 'jornada';
  const recorte = umMes ? 'mês' : 'período';

  const stages = useMemo(
    () => aplicarContagemFunil(computeEventFunnel(dadosFiltrados, periodo, customRange, { incluirSaidas: true }), contagem, periodo, customRange),
    [dadosFiltrados, periodo, customRange, contagem]
  );

  const stagesComparacao = useMemo(() => {
    if (!filtros.periodoComparacao) return null;
    const janelaB = calcularJanelaPeriodoDashboard(filtros.periodoComparacao, hoje);
    const { periodo: periodoB, customRange: customRangeB } = janelaParaPeriodoComercial(janelaB, hoje);
    return aplicarContagemFunil(computeEventFunnel(dadosFiltrados, periodoB, customRangeB, { incluirSaidas: true }), contagem, periodoB, customRangeB);
  }, [dadosFiltrados, filtros.periodoComparacao, hoje, contagem]);

  // ⚠️ TODO hook fica ACIMA dos `return` de erro/carregamento. Com um useMemo
  // abaixo deles, a contagem de hooks muda entre o render "carregando" e o
  // render com dados, e o React quebra no meio da atualização — a tela
  // congelava nos números anteriores, e trocar o canal não mudava nada.
  const stageAberta = stages.find((s) => s.key === etapaAberta) ?? null;
  // A lista sai da MESMA etapa que somou o card — inclusive a data, que é a do
  // evento daquela etapa e não a de criação da negociação.
  const linhasAbertas: LinhaNegociacao[] = useMemo(
    () => (stageAberta ? linhasDaEtapa(stageAberta, profilesById) : []),
    [stageAberta, profilesById],
  );

  // Uma passada por etapa, reaproveitando o mesmo achatamento da lista.
  const resumoPorEtapa = useMemo(() => {
    const mapa = new Map<string, { concentracao: ConcentracaoKwh | null; vindasDeOutroMes: number }>();
    for (const s of stages) {
      const linhas = linhasDaEtapa(s, profilesById);
      mapa.set(s.key, {
        concentracao: concentracaoKwh(linhas),
        vindasDeOutroMes: linhas.filter((l) => l.atravessouMes).length,
      });
    }
    return mapa;
  }, [stages, profilesById]);

  // kWh com TERMO ASSINADO no período, por canal — o seletor mostra o peso de
  // cada canal sem ninguém ter de marcar e desmarcar para descobrir.
  //
  // Sai de `dados` (todos os canais), não de `dadosFiltrados`: cada opção tem de
  // mostrar o próprio número mesmo desmarcada. As linhas são as MESMAS que o
  // card "Termo Assinado" soma, então com todos os canais marcados o total do
  // seletor bate com o card.
  const volumePorCanal = useMemo(() => {
    const assinadas = aplicarContagemFunil(computeEventFunnel(dados, periodo, customRange), contagem, periodo, customRange)
      .find((s) => s.key === 'termo_assinado')?.rows ?? [];
    return volumeKwhPorCanal(assinadas, profilesById);
  }, [dados, periodo, customRange, contagem, profilesById]);

  if (erro) {
    return <p className="text-sm font-semibold text-red-600 dark:text-red-400">{erro}</p>;
  }

  if (loading && dados.length === 0) {
    return <LoadingSpinner label="Carregando seção Comercial..." />;
  }

  const exportarLista = async (visiveis: LinhaNegociacao[]) => {
    if (!stageAberta) return;
    setExportando(true);
    try {
      await baixarPlanilhas(
        [planilhaNegociacoes(visiveis)],
        `negociacoes_${slugArquivo(stageAberta.label)}_${slugArquivo(rotuloPeriodoDashboard(filtros.periodo))}${soNoPeriodo ? `_so_no_${recorte === 'mês' ? 'mes' : 'periodo'}` : ''}`,
      );
    } catch (e: any) {
      console.error('Erro ao exportar as negociações da etapa:', e);
      addToast(e?.message || 'Não foi possível gerar o Excel.', 'error');
    } finally {
      setExportando(false);
    }
  };

  const itensComparativo: ItemComparativo[] = stagesComparacao
    ? stages.map((s, i) => ({
        titulo: s.tipo === 'saida' ? `${s.label} (saída)` : s.label,
        // Mais reprovados é pior: o comparativo inverte a cor da variação.
        inverso: s.tipo === 'saida',
        valorAtual: s.leads,
        valorComparacao: stagesComparacao[i]?.leads ?? 0,
        formatar: fmtNum,
      }))
    : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-2 lg:gap-4">
        <h2 className="text-sm font-bold text-gray-800 dark:text-white">Comercial</h2>
        <div className="lg:ml-auto flex flex-wrap items-center gap-x-4 gap-y-2">
          <SeletorContagemFunil
            valor={contagem}
            onChange={(contagemComercial) => onChange({ ...filtros, contagemComercial })}
            umMes={umMes}
          />
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Canal de venda</span>
            <SeletorDepartamentos
              selecionados={filtros.departamentos}
              onChange={(departamentos) => onChange({ ...filtros, departamentos })}
              volumeKwh={volumePorCanal}
              legendaVolume={`kWh com termo assinado · ${rotuloPeriodoDashboard(filtros.periodo)}${soNoPeriodo ? ` · só o que aconteceu no ${recorte}` : ''}`}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {stages.map((s) => {
          const taxa = s.tipo === 'saida' ? taxaReprovacao(stages) : null;
          return (
            <CardComercial
              key={s.key}
              titulo={s.label}
              qtde={s.leads}
              kwh={s.kwh}
              resumo={s.tipo === 'saida' ? null : resumoPorEtapa.get(s.key) ?? null}
              onClick={() => setEtapaAberta(s.key)}
              saida={s.tipo === 'saida'}
              nota={s.tipo === 'saida'
                ? (taxa != null ? `Saída do funil · ${taxa.toFixed(1).replace('.', ',')}% das decisões do período` : 'Saída do funil · sem decisões no período')
                : undefined}
            />
          );
        })}
      </div>

      {stagesComparacao && filtros.periodoComparacao && (
        <ComparativoResumo rotuloComparacao={rotuloPeriodoDashboard(filtros.periodoComparacao)} itens={itensComparativo} />
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
        <h3 className="text-sm font-bold text-gray-800 dark:text-white mb-1">Funil Comercial</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
          {soNoPeriodo
            ? `Só o que aconteceu no ${recorte}: a negociação só conta numa etapa se todas as etapas, da criação até ela, caíram dentro do ${recorte}. Cada etapa fica dentro da anterior.`
            : 'Cada etapa conta pela data em que ela aconteceu, não pela data de criação da negociação.'}
          {' '}"Termo Reprovado" é saída do funil, não a etapa seguinte à aprovação.
        </p>
        <FunnelChart stages={stages} />
      </div>

      <ListaNegociacoesModal
        aberto={!!stageAberta}
        etapa={stageAberta?.label ?? ''}
        descricao={[
          rotuloPeriodoDashboard(filtros.periodo),
          stageAberta?.tipo === 'saida'
            ? 'conta pela data da reprovação, mesmo que a negociação tenha sido recuperada depois. Por isso pode diferir de "reprovados" no Analytics do CRM, que conta quem está reprovado hoje, sem período.'
            : 'conta pela data em que a etapa aconteceu, não pela criação da negociação',
          ...(soNoPeriodo ? [`só as negociações com todas as etapas anteriores, da criação em diante, dentro do ${recorte}`] : []),
        ].join(' · ')}
        linhas={linhasAbertas}
        exportando={exportando}
        onFechar={() => setEtapaAberta(null)}
        onExportar={exportarLista}
      />
    </div>
  );
};

export default SecaoComercial;
